import React, { useState, useMemo, useCallback, useEffect } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Videocam as CameraIcon,
  Image as ImagePlaceholderIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CalendarToday as CalendarIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Map as MapIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  RecyclingOutlined as RecyclingOutlinedIcon,
} from "@mui/icons-material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  CARD_TITLE_STYLE,
  CARD_TITLE_SPACING,
} from "../../constants/cardStyles.ts";
import { GLOBAL_TOOLTIP_PROPS } from "../../config/tooltips.config.ts";
import { resolveCameraMediaUrl } from "../../services/cameraDetailApi.ts";
import { cameraPreviewPath } from "../../utils/alertDetectionMedia";
import { AlertHistoryMediaImage } from "../alertServicesSections/AlertHistoryAuthenticatedMedia.jsx";
import { readSrIdFromUnknown } from "../../utils/srIdDisplay.ts";
import { ViewToggle } from "./ViewToggle.tsx";
import { ZoneConfigurationPageNew } from "./ZoneConfigurationPageNew.tsx";
import {
  AddCameraDialog,
  type AddCameraFormValues,
} from "./AddCameraDialog.tsx";
import { useModuleCrudPermissions } from "../../hooks/useModuleCrudPermissions.js";

declare const process: { env: Record<string, string | undefined> };

/** Base URL for resolving relative `preview_url` paths from GET /cameras. */
const AI_DETECTION_API_BASE =
  process.env.REACT_APP_API_URL_FOR_AI_DETECTION ?? "";

/** Chevron + clear chip; matches Recycle Bin so labels do not overlap the X / dropdown. */
const ZONE_MGMT_FILTER_SELECT_PR_CLEAR = "76px";
const ZONE_MGMT_FILTER_CLEAR_BTN_RIGHT_PX = 36;

export interface CameraZone {
  id: string;
  /** Backend `sr_id` — preferred label in ID column */
  srId?: string;
  /** When one camera has multiple zone rows, PATCH /cameras/{id} uses this (falls back to `id`). */
  cameraApiId?: string;
  cameraGroup: string;
  cameraName: string;
  /** From GET /cameras `camera_ip` — used in zone config / fallbacks where IP is needed. */
  cameraIp: string;
  /** From GET /cameras `streaming_url` — shown in Zone Management table/grid. */
  streamingUrl?: string;
  zone?: number;
  useCase: string;
  status: "Active" | "Inactive";
  enabled: boolean;
  isFavorite?: boolean;
  uploadedDate: Date;
  /** Snapshot path from API (`preview_url`); may be relative to AI detection API base. */
  preview_url?: string | null;
}

/** Persisted favorite row ids (works for API-driven rows where `zones` state is not the data source). */
const CAMERA_ZONES_FAVORITE_IDS_KEY = "camera-zones-favorite-ids";

const CAMERA_ZONES_DATE_FILTER_LS_KEY = "camera-zones-date-filter";
const CAMERA_ZONES_CUSTOM_RANGE_LS_KEY = "camera-zones-custom-date-range";

/**
 * HTML date inputs use YYYY-MM-DD. Parsing with `new Date(iso)` is UTC and shifts the calendar day
 * in many local timezones; use local midnight / end-of-day instead.
 */
function startOfLocalDayFromIso(isoDate: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (
    Number.isNaN(y) ||
    Number.isNaN(mo) ||
    Number.isNaN(d) ||
    mo < 1 ||
    mo > 12 ||
    d < 1 ||
    d > 31
  ) {
    return null;
  }
  const dt = new Date(y, mo - 1, d, 0, 0, 0, 0);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== mo - 1 ||
    dt.getDate() !== d
  ) {
    return null;
  }
  return dt;
}

function endOfLocalDayFromIso(isoDate: string): Date | null {
  const start = startOfLocalDayFromIso(isoDate);
  if (!start) return null;
  return new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
    23,
    59,
    59,
    999,
  );
}

function loadPersistedDateFilterState(): {
  dateFilter: string;
  customStartDate: string;
  customEndDate: string;
} {
  try {
    const dateFilter =
      localStorage.getItem(CAMERA_ZONES_DATE_FILTER_LS_KEY) || "all";
    if (dateFilter === "custom") {
      const raw = localStorage.getItem(CAMERA_ZONES_CUSTOM_RANGE_LS_KEY);
      if (raw) {
        const p = JSON.parse(raw) as { start?: unknown; end?: unknown };
        const start = typeof p.start === "string" ? p.start : "";
        const end = typeof p.end === "string" ? p.end : "";
        if (start || end) {
          return {
            dateFilter: "custom",
            customStartDate: start,
            customEndDate: end,
          };
        }
      }
      return { dateFilter: "all", customStartDate: "", customEndDate: "" };
    }
    return { dateFilter, customStartDate: "", customEndDate: "" };
  } catch {
    return { dateFilter: "all", customStartDate: "", customEndDate: "" };
  }
}

function loadFavoriteIdsFromStorage(): Set<string> {
  try {
    const raw = localStorage.getItem(CAMERA_ZONES_FAVORITE_IDS_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as unknown;
      if (Array.isArray(arr)) return new Set(arr.map(String));
    }
    const zonesRaw = localStorage.getItem("camera-zones-data");
    if (zonesRaw) {
      const parsed = JSON.parse(zonesRaw) as unknown;
      if (Array.isArray(parsed)) {
        const ids = (parsed as CameraZone[])
          .filter((z) => z && Boolean(z.isFavorite))
          .map((z) => String(z.id));
        if (ids.length > 0) return new Set(ids);
      }
    }
  } catch (e) {
    console.error("[CameraZonesTable] Failed to load favorite row ids", e);
  }
  return new Set();
}

// Dummy data - Use Case column is empty by default
const INITIAL_ZONES: CameraZone[] = [];

type ViewMode = "list" | "grid";
type Order = "asc" | "desc";
/** Sortable table columns only (excludes `preview_url`, favorites, etc.). */
type OrderBy =
  | "id"
  | "cameraGroup"
  | "cameraName"
  | "streamingUrl"
  | "zone"
  | "useCase"
  | "status";

/** API item from GET /cameras — legacy camera row or zone-config row with use_cases (camera_id, zone_id, …). */
export interface CameraApiItem {
  id?: string;
  camera_id?: string;
  zone_id?: string;
  zone_name?: string | null;
  name?: string;
  camera_ip?: string;
  streaming_url?: string;
  /** Legacy field from older APIs; ignored when `streaming_url` is present. */
  stream_url?: string;
  is_active?: boolean;
  zones_count?: number;
  preview_url?: string | null;
  /** Some backends send this casing; treated same as `preview_url`. */
  preview_Url?: string | null;
  created_at?: string;
  updated_at?: string;
  use_cases?: Array<{
    use_case_id?: string;
    use_case_name?: string | null;
    zone_profile_id?: string | null;
    severity?: string;
    deleted_at?: string | null;
    profile_count?: number;
  }>;
  sr_id?: string | number;
}

/** Join use_case_name values; missing names shown as "—". */
export function formatUseCasesDisplayFromApi(use_cases: unknown): string {
  if (!Array.isArray(use_cases) || use_cases.length === 0) return "";
  const parts: string[] = [];
  for (const uc of use_cases) {
    if (!uc || typeof uc !== "object") {
      parts.push("—");
      continue;
    }
    const n = (uc as { use_case_name?: string | null }).use_case_name;
    if (n == null || String(n).trim() === "") parts.push("—");
    else parts.push(String(n).trim());
  }
  return parts.join(", ");
}

/** True when the row has no real use-case label (matches Use case column “—” cases). */
function rowUseCaseIsUnassigned(useCase: string | undefined): boolean {
  const s = (useCase ?? "").trim();
  if (!s) return true;
  return s.split(",").every((part) => {
    const t = part.trim();
    return t === "" || t === "—" || t === "-";
  });
}

function isZoneConfigStyleApiItem(c: CameraApiItem): boolean {
  return Boolean(
    c.zone_id || (Array.isArray(c.use_cases) && c.use_cases.length > 0),
  );
}

/**
 * Zone column for GET /cameras: use backend `zones_count` when provided.
 * Only falls back to `use_cases.length` when `zones_count` is missing (legacy rows).
 */
function zoneCountFromCameraApiItem(c: CameraApiItem): number | undefined {
  if (typeof c.zones_count === "number" && !Number.isNaN(c.zones_count)) {
    return c.zones_count;
  }
  const zoneConfigStyle = isZoneConfigStyleApiItem(c);
  if (zoneConfigStyle && Array.isArray(c.use_cases) && c.use_cases.length > 0) {
    return c.use_cases.length;
  }
  return undefined;
}

function displayNameFromZoneApiItem(c: CameraApiItem): string {
  if (c.zone_name != null && String(c.zone_name).trim() !== "")
    return String(c.zone_name).trim();
  if (c.name != null && String(c.name).trim() !== "")
    return String(c.name).trim();
  if (c.camera_id != null && String(c.camera_id).trim() !== "")
    return String(c.camera_id).trim();
  if (c.id != null && String(c.id).trim() !== "") return String(c.id).trim();
  return "";
}

/** First non-empty preview path from API (`preview_url` or `preview_Url`). */
function previewPathFromCameraApiItem(c: CameraApiItem): string | null {
  const v = c.preview_url ?? c.preview_Url;
  if (v == null || String(v).trim() === "") return null;
  return String(v).trim();
}

interface CameraZonesTableProps {
  title?: string;
  onZoneConfigChange?: (isActive: boolean) => void;
  cameras?: CameraApiItem[];
  /** When provided, API error is shown in a centered box (same as Manage Use Case tab) */
  camerasError?: string | null;
  /** When true, shows loading state instead of table/grid */
  camerasLoading?: boolean;
  /** Retry callback for error state */
  onRetry?: () => void;
  /** Called when camera active toggle is changed (Zone Management). Parent should PATCH /cameras/{id} and refetch. */
  onCameraToggle?: (
    cameraId: string,
    isActive: boolean,
  ) => void | Promise<void>;
  /** Opens Zone Management settings (same pattern as other tabs). */
  onSettingsClick?: () => void;
  /**
   * Passed into zone configuration: GET /cameras runs when the user leaves via the header back control (before the panel closes).
   * Use to refetch so `zones_count` and related fields stay in sync.
   */
  onAfterZoneConfigSave?: () => void | Promise<void>;
  /** Register a new camera (opens Add Camera dialog; Apply calls this). */
  onAddCameraApply?: (values: AddCameraFormValues) => void | Promise<void>;
  /** Update camera (same payload as create); row menu "Edit Camera". */
  onEditCameraApply?: (
    cameraId: string,
    values: AddCameraFormValues,
  ) => void | Promise<void>;
  /**
   * Reserved for when row "Delete Camera" calls DELETE /cameras/{id}.
   * Currently the menu shows an "Upcoming feature" dialog instead.
   */
  onDeleteCamera?: (cameraId: string) => void | Promise<void>;
}

/** Returns empty string for null/undefined/missing; otherwise String(value). Use for table cells. */
function cell(value: unknown): string {
  if (value == null || value === "") return "";
  return String(value);
}

/** Table cell display: hyphen when value is missing or blank (e.g. Camera Group). */
function cellOrDash(value: unknown): string {
  const s = cell(value).trim();
  return s !== "" ? s : "-";
}

/**
 * Row actions (Edit / Delete camera) need a stable API id for GET/PUT/DELETE `/cameras/{id}`.
 * Accepts standard UUIDs and other opaque ids (no spaces/slashes) so backends are not forced to UUID-only.
 */
function isUsableCameraApiId(v: string | undefined): boolean {
  if (!v || typeof v !== "string") return false;
  const s = v.trim();
  if (s.length === 0 || s.length > 200) return false;
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      s,
    )
  ) {
    return true;
  }
  return /^[a-zA-Z0-9_-]+$/.test(s) && s.length >= 4;
}

export function CameraZonesTable({
  title = "Manage Zones",
  onZoneConfigChange,
  cameras,
  camerasError,
  camerasLoading,
  onRetry,
  onCameraToggle,
  onSettingsClick,
  onAfterZoneConfigSave,
  onAddCameraApply,
  onEditCameraApply,
  onDeleteCamera,
}: CameraZonesTableProps) {
  const { create: canCreateCamera, update: canUpdateCamera, delete: canDeleteCamera } =
    useModuleCrudPermissions("cameras");
  const { update: canUpdateZone } = useModuleCrudPermissions("zones");
  const [addCameraOpen, setAddCameraOpen] = useState(false);
  const [editingCameraId, setEditingCameraId] = useState<string | null>(null);
  const [deleteUpcomingFeatureOpen, setDeleteUpcomingFeatureOpen] =
    useState(false);

  const openAddCameraDialog = useCallback(() => {
    setEditingCameraId(null);
    setAddCameraOpen(true);
  }, []);

  const closeCameraDialog = useCallback(() => {
    setAddCameraOpen(false);
    setEditingCameraId(null);
  }, []);
  // Load zones from localStorage or use INITIAL_ZONES
  const [zones, setZones] = useState<CameraZone[]>(() => {
    try {
      const savedZones = localStorage.getItem("camera-zones-data");
      if (savedZones) {
        return JSON.parse(savedZones);
      }
    } catch (error) {
      console.error("Error loading zones from localStorage:", error);
    }
    return INITIAL_ZONES;
  });

  // Save zones to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem("camera-zones-data", JSON.stringify(zones));
    } catch (error) {
      console.error("Error saving zones to localStorage:", error);
    }
  }, [zones]);

  // New preview URLs from API refetch should retry thumbnails
  useEffect(() => {
    setGridThumbBrokenIds(new Set());
  }, [cameras]);

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(14);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<OrderBy>("cameraName");
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  /** Grid preview image failed to load — show placeholder for that row. */
  const [gridThumbBrokenIds, setGridThumbBrokenIds] = useState<Set<string>>(
    () => new Set(),
  );
  /** Row actions menu: opened via 3-dot button or right-click (list + grid). */
  const [anchorEl, setAnchorEl] = useState<{
    zone: CameraZone;
    mouseX: number;
    mouseY: number;
  } | null>(null);

  // Favorites filter with localStorage persistence
  const [showFavoritesOnly, setShowFavoritesOnly] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem("camera-zones-show-favorites");
      return saved === "true";
    } catch {
      return false;
    }
  });

  // Persist favorites filter
  useEffect(() => {
    try {
      localStorage.setItem(
        "camera-zones-show-favorites",
        String(showFavoritesOnly),
      );
    } catch (error) {
      console.error("Error saving favorites filter:", error);
    }
  }, [showFavoritesOnly]);

  /** Favorites by row id — used when table data comes from `cameras` API (not `zones` state). */
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(
    loadFavoriteIdsFromStorage,
  );

  useEffect(() => {
    try {
      localStorage.setItem(
        CAMERA_ZONES_FAVORITE_IDS_KEY,
        JSON.stringify([...favoriteIds]),
      );
    } catch (e) {
      console.error("[CameraZonesTable] Failed to persist favorite row ids", e);
    }
  }, [favoriteIds]);

  // View mode state with localStorage persistence
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const saved = localStorage.getItem("camera-zones-view-mode");
      return (saved as ViewMode) || "list";
    } catch {
      return "list";
    }
  });

  // Persist view mode changes
  useEffect(() => {
    try {
      localStorage.setItem("camera-zones-view-mode", viewMode);
    } catch (error) {
      console.error("Error saving view mode:", error);
    }
  }, [viewMode]);

  const handleViewModeChange = useCallback((newMode: ViewMode) => {
    setViewMode(newMode);
  }, []);

  // Date Filter with localStorage persistence (custom range stored alongside filter value)
  const [initialDatePersisted] = useState(() => loadPersistedDateFilterState());
  const [dateFilter, setDateFilter] = useState(initialDatePersisted.dateFilter);
  const [customStartDate, setCustomStartDate] = useState(
    initialDatePersisted.customStartDate,
  );
  const [customEndDate, setCustomEndDate] = useState(
    initialDatePersisted.customEndDate,
  );

  useEffect(() => {
    try {
      localStorage.setItem(CAMERA_ZONES_DATE_FILTER_LS_KEY, dateFilter);
      if (dateFilter === "custom" && (customStartDate || customEndDate)) {
        localStorage.setItem(
          CAMERA_ZONES_CUSTOM_RANGE_LS_KEY,
          JSON.stringify({
            start: customStartDate,
            end: customEndDate,
          }),
        );
      } else {
        localStorage.removeItem(CAMERA_ZONES_CUSTOM_RANGE_LS_KEY);
      }
    } catch (error) {
      console.error("Error saving date filter:", error);
    }
  }, [dateFilter, customStartDate, customEndDate]);

  // Use Case Filter with localStorage persistence
  const [useCaseFilter, setUseCaseFilter] = useState<string>(() => {
    try {
      const saved = localStorage.getItem("camera-zones-use-case-filter");
      return saved || "all";
    } catch {
      return "all";
    }
  });

  // Persist use case filter
  useEffect(() => {
    try {
      localStorage.setItem("camera-zones-use-case-filter", useCaseFilter);
    } catch (error) {
      console.error("Error saving use case filter:", error);
    }
  }, [useCaseFilter]);

  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);

  // Zone Configuration Page state
  const [selectedZoneForConfig, setSelectedZoneForConfig] =
    useState<CameraZone | null>(null);
  /** True when opened via "View Details" (read-only Zones + AI Models actions). */
  const [zoneConfigReadOnly, setZoneConfigReadOnly] = useState(false);

  // Handle date filter change
  const handleDateFilterChange = (value: string) => {
    if (value === "custom") {
      setDateFilter("custom");
      setCustomDateDialogOpen(true);
      setPage(0);
    } else {
      setDateFilter(value);
      setCustomStartDate("");
      setCustomEndDate("");
      setPage(0);
    }
  };

  const handleApplyCustomDateRange = () => {
    if (!customStartDate.trim() && !customEndDate.trim()) return;
    setDateFilter("custom");
    setCustomDateDialogOpen(false);
    setPage(0);
  };

  const handleCustomDialogClose = () => {
    setCustomDateDialogOpen(false);
    if (!customStartDate.trim() && !customEndDate.trim()) {
      setDateFilter("all");
    }
  };

  // Clear date filter
  const handleClearDateFilter = () => {
    setDateFilter("all");
    setCustomStartDate("");
    setCustomEndDate("");
    setPage(0);
    try {
      localStorage.removeItem(CAMERA_ZONES_CUSTOM_RANGE_LS_KEY);
    } catch {
      /* ignore */
    }
  };

  // Clear use case filter
  const handleClearUseCaseFilter = () => {
    setUseCaseFilter("all");
    setPage(0);
  };

  // Filter zones by date
  const filterByDate = useCallback(
    (zone: CameraZone) => {
      if (dateFilter === "all") return true;

      const now = new Date();
      const uploadDate = new Date(zone.uploadedDate);

      switch (dateFilter) {
        case "today": {
          const today = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
          );
          return uploadDate >= today;
        }
        case "last7days": {
          const sevenDaysAgo = new Date(now);
          sevenDaysAgo.setDate(now.getDate() - 7);
          return uploadDate >= sevenDaysAgo;
        }
        case "last30days": {
          const thirtyDaysAgo = new Date(now);
          thirtyDaysAgo.setDate(now.getDate() - 30);
          return uploadDate >= thirtyDaysAgo;
        }
        case "custom": {
          if (!customStartDate.trim() && !customEndDate.trim()) return true;
          let startMs: number;
          let endMs: number;
          if (customStartDate.trim()) {
            const s = startOfLocalDayFromIso(customStartDate);
            if (!s) return false;
            startMs = s.getTime();
          } else {
            startMs = 0;
          }
          if (customEndDate.trim()) {
            const e = endOfLocalDayFromIso(customEndDate);
            if (!e) return false;
            endMs = e.getTime();
          } else {
            endMs = Date.now();
          }
          if (startMs > endMs) {
            const t = startMs;
            startMs = endMs;
            endMs = t;
          }
          const t = uploadDate.getTime();
          if (Number.isNaN(t)) return false;
          return t >= startMs && t <= endMs;
        }
        default:
          return true;
      }
    },
    [dateFilter, customStartDate, customEndDate],
  );

  // Handle sort
  const handleSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Handle toggle: when cameras from API are used, call onCameraToggle (parent does PATCH); otherwise update local state.
  const handleToggle = (zone: CameraZone) => {
    const id = zone.cameraApiId ?? zone.id;
    const nextActive = !zone.enabled;
    if (cameras && cameras.length > 0 && onCameraToggle) {
      onCameraToggle(id, nextActive);
      return;
    }
    setZones((prevZones) =>
      prevZones.map((z) =>
        z.id === id
          ? {
              ...z,
              enabled: nextActive,
              status: nextActive ? "Active" : "Inactive",
            }
          : z,
      ),
    );
  };

  // Handle toggle favorite (persisted by row id — works for API and local rows)
  const handleToggleFavorite = (id: string, event?: React.MouseEvent) => {
    event?.stopPropagation();
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setZones((prevZones) =>
      prevZones.map((zone) =>
        zone.id === id ? { ...zone, isFavorite: !zone.isFavorite } : zone,
      ),
    );
  };

  // Handle right-click: same actions menu as the 3-dot control (cursor position)
  const handleContextMenu = (event: React.MouseEvent, zone: CameraZone) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl({
      zone,
      mouseX: event.clientX,
      mouseY: event.clientY,
    });
  };

  /**
   * Open Zone Configuration. `readOnly` is true for "View Details" only (no add/edit/delete in Zones + AI Models).
   */
  const openZoneConfiguration = (zone: CameraZone, readOnly: boolean) => {
    setSelectedZoneForConfig(zone);
    setZoneConfigReadOnly(readOnly);
    onZoneConfigChange?.(true);
  };

  // Handle menu
  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    zone: CameraZone,
  ) => {
    event.stopPropagation();
    setAnchorEl({
      zone,
      mouseX: event.clientX,
      mouseY: event.clientY,
    });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // When cameras from API are provided, use them; when API returns empty, use [] (no dummy data). Otherwise use zones from state.
  const displayRows = useMemo((): CameraZone[] => {
    const withFavoriteFlag = (row: CameraZone): CameraZone => ({
      ...row,
      isFavorite: favoriteIds.has(row.id),
    });

    if (cameras && cameras.length > 0) {
      return cameras.map((c, index) => {
        const zoneConfigStyle = isZoneConfigStyleApiItem(c);
        const cameraApiId = cell(c.camera_id || c.id);
        const rowId = zoneConfigStyle
          ? cell(c.zone_id) || `${cameraApiId || "cam"}-${index}`
          : cell(c.id) || cameraApiId || `row-${index}`;
        const useCaseText = zoneConfigStyle
          ? formatUseCasesDisplayFromApi(c.use_cases)
          : "";
        const row: CameraZone = {
          id: rowId,
          srId: readSrIdFromUnknown(c),
          cameraApiId: cameraApiId || undefined,
          cameraGroup: "",
          cameraName: zoneConfigStyle
            ? displayNameFromZoneApiItem(c)
            : cell(c.name),
          cameraIp: cell(c.camera_ip),
          streamingUrl: cell(c.streaming_url ?? c.stream_url),
          zone: zoneCountFromCameraApiItem(c),
          useCase: useCaseText,
          status: (c.is_active ? "Active" : "Inactive") as
            | "Active"
            | "Inactive",
          enabled: Boolean(c.is_active),
          isFavorite: false,
          uploadedDate: c.created_at ? new Date(c.created_at) : new Date(),
          preview_url: previewPathFromCameraApiItem(c),
        };
        return withFavoriteFlag(row);
      });
    }
    if (cameras && cameras.length === 0) {
      return []; // API returned no zones – do not show dummy data
    }
    return zones.map(withFavoriteFlag);
  }, [cameras, zones, favoriteIds]);

  /** Distinct Use case column strings from current table data (for filter dropdown). */
  const useCaseFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const z of displayRows) {
      const u = (z.useCase ?? "").trim();
      if (u && !rowUseCaseIsUnassigned(z.useCase)) set.add(u);
    }
    return [...set].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }, [displayRows]);

  const hasEmptyUseCaseInTable = useMemo(
    () => displayRows.some((z) => rowUseCaseIsUnassigned(z.useCase)),
    [displayRows],
  );

  // Reset persisted filter when it no longer exists in current data
  useEffect(() => {
    if (useCaseFilter === "all") return;
    if (useCaseFilter === "") {
      if (!hasEmptyUseCaseInTable) setUseCaseFilter("all");
      return;
    }
    if (!useCaseFilterOptions.includes(useCaseFilter)) {
      setUseCaseFilter("all");
    }
  }, [useCaseFilter, useCaseFilterOptions, hasEmptyUseCaseInTable]);

  // Filter and sort data
  const filteredAndSortedZones = useMemo(() => {
    let filtered = displayRows.filter((zone) => {
      const query = searchQuery.toLowerCase();
      return (
        (zone.id ?? "").toLowerCase().includes(query) ||
        (zone.srId ?? "").toLowerCase().includes(query) ||
        (zone.cameraGroup ?? "").toLowerCase().includes(query) ||
        (zone.cameraName ?? "").toLowerCase().includes(query) ||
        (zone.streamingUrl ?? "").toLowerCase().includes(query) ||
        (zone.useCase ?? "").toLowerCase().includes(query) ||
        (zone.status ?? "").toLowerCase().includes(query)
      );
    });

    // Apply favorites filter
    if (showFavoritesOnly) {
      filtered = filtered.filter((zone) => zone.isFavorite);
    }

    // Apply date filter
    filtered = filtered.filter(filterByDate);

    // Apply use case filter (values match Use case column text)
    if (useCaseFilter !== "all") {
      if (useCaseFilter === "") {
        filtered = filtered.filter((zone) =>
          rowUseCaseIsUnassigned(zone.useCase),
        );
      } else {
        filtered = filtered.filter(
          (zone) =>
            (zone.useCase ?? "").toLowerCase() === useCaseFilter.toLowerCase(),
        );
      }
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Treat undefined as sortable (push to end)
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return order === "asc" ? 1 : -1;
      if (bValue === undefined) return order === "asc" ? -1 : 1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return order === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === "asc" ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [
    displayRows,
    searchQuery,
    order,
    orderBy,
    showFavoritesOnly,
    dateFilter,
    useCaseFilter,
    filterByDate,
  ]);

  // Paginate
  const paginatedZones = useMemo(() => {
    return filteredAndSortedZones.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredAndSortedZones, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Truncate text helper
  const truncateText = (text: string, maxLength: number = 20) => {
    if (text === "--" || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Calculate favorites count (current data source rows only)
  const favoritesCount = useMemo(
    () => displayRows.filter((zone) => favoriteIds.has(zone.id)).length,
    [displayRows, favoriteIds],
  );

  // If zone is selected for configuration, show the configuration page instead of table
  if (selectedZoneForConfig) {
    return (
      <ZoneConfigurationPageNew
        cameraZone={selectedZoneForConfig}
        readOnly={zoneConfigReadOnly || !canUpdateZone}
        onAddCameraApply={canCreateCamera ? onAddCameraApply : undefined}
        onRefetchCamerasList={onAfterZoneConfigSave}
        onSave={(updatedZone) => {
          // Update the zone in the zones array. ZoneConfigurationPageNew passes back the same zone object we passed in.
          setZones((prevZones) =>
            prevZones.map((z) =>
              z.id === updatedZone.id
                ? (updatedZone as unknown as CameraZone)
                : z,
            ),
          );
          setSelectedZoneForConfig(null);
          setZoneConfigReadOnly(false);
          onZoneConfigChange?.(false);
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      {/* Header — padding/layout aligned with ManageUseCaseTable */}
      <Box sx={{ borderBottom: "1px solid #E5E7EB", flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            px: { xs: 1, sm: 2 },
            pt: 2,
            pb: 1.5,
          }}
        >
          <Typography
            sx={{
              ...CARD_TITLE_STYLE,
              fontSize: { xs: "14px", sm: "16px" },
              flex: 1,
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
          {onSettingsClick ? (
            <Tooltip title="Zone Management settings" {...GLOBAL_TOOLTIP_PROPS}>
              <IconButton
                size="small"
                onClick={onSettingsClick}
                aria-label="Open Zone Management settings"
                sx={{
                  flexShrink: 0,
                  border: "1px solid #E5E7EB",
                  borderRadius: "10px",
                  width: 36,
                  height: 36,
                  color: "#4A5565",
                  "&:hover": {
                    bgcolor: "#f5f5f5",
                    color: "#2932E5",
                  },
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            px: { xs: 1, sm: 2 },
            pb: 2,
          }}
        >
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search zones..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: "240px" },
              minWidth: { xs: 0, sm: 240 },
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              "& .MuiOutlinedInput-root": {
                height: 36,
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                bgcolor: "white",
                padding: 0,
                borderRadius: "10px",
                "& fieldset": {
                  borderColor: "#E5E7EB",
                },
              },
            }}
          />

          {/* Favorites Button with Badge */}
          <Tooltip
            title={showFavoritesOnly ? "Show all zones" : "Show only favorites"}
            {...GLOBAL_TOOLTIP_PROPS}
          >
            <IconButton
              size="small"
              onClick={() => {
                setShowFavoritesOnly(!showFavoritesOnly);
                setPage(0);
              }}
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "10px",
                padding: "6px",
                bgcolor: showFavoritesOnly ? "#FFE0D0" : "white",

                "&:hover": {
                  bgcolor: showFavoritesOnly ? "#FFE0D0" : "#f5f5f5",
                },
              }}
            >
              <Badge
                badgeContent={favoritesCount}
                color="primary"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "10px",
                    height: "16px",
                    minWidth: "16px",
                    bgcolor: "#2932E5",
                    borderRadius: "10px",
                  },
                }}
              >
                {showFavoritesOnly ? (
                  <StarIcon
                    fontSize="small"
                    sx={{ color: "#FF8555", borderRadius: "10px" }}
                  />
                ) : (
                  <StarBorderIcon
                    fontSize="small"
                    sx={{ color: "#4A5565", borderRadius: "10px" }}
                  />
                )}
              </Badge>
            </IconButton>
          </Tooltip>

          {/* View Toggle */}
          <ViewToggle
            view={viewMode}
            onChange={handleViewModeChange}
            ariaLabel="Toggle camera zones view mode"
          />

          {/* Date Filter — clear absolutely positioned (same pattern as Recycle Bin) */}
          <FormControl
            size="small"
            sx={{ minWidth: 160, position: "relative" }}
          >
            <Select
              value={dateFilter}
              onChange={(e) => handleDateFilterChange(e.target.value)}
              displayEmpty
              startAdornment={
                <CalendarIcon
                  fontSize="small"
                  sx={{ mr: 0.5, color: "#4A5565" }}
                />
              }
              sx={{
                height: 36,
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                borderRadius: "10px",
                bgcolor: dateFilter !== "all" ? "#FFE0D0" : "white",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  paddingRight:
                    dateFilter !== "all"
                      ? `${ZONE_MGMT_FILTER_SELECT_PR_CLEAR} !important`
                      : undefined,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&:hover": {
                  bgcolor: dateFilter !== "all" ? "#FFE0D0" : "#f5f5f5",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2932E5",
                },
              }}
            >
              <MenuItem value="all">All Dates</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="last7days">Last 7 Days</MenuItem>
              <MenuItem value="last30days">Last 30 Days</MenuItem>
              <MenuItem value="custom">Custom Range...</MenuItem>
            </Select>
            {dateFilter !== "all" ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClearDateFilter();
                }}
                aria-label="Clear date filter"
                sx={{
                  position: "absolute",
                  right: `${ZONE_MGMT_FILTER_CLEAR_BTN_RIGHT_PX}px`,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  p: 0.25,
                  width: 28,
                  height: 28,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null}
          </FormControl>

          {/* Use Case Filter */}
          <FormControl
            size="small"
            sx={{ minWidth: 180, position: "relative" }}
          >
            <Select
              value={useCaseFilter}
              onChange={(e) => {
                setUseCaseFilter(e.target.value);
                setPage(0);
              }}
              displayEmpty
              startAdornment={
                <FilterListIcon
                  fontSize="small"
                  sx={{ mr: 0.5, color: "#4A5565" }}
                />
              }
              sx={{
                borderRadius: "10px",
                height: 36,
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                bgcolor: useCaseFilter !== "all" ? "#FFE0D0" : "white",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  paddingRight:
                    useCaseFilter !== "all"
                      ? `${ZONE_MGMT_FILTER_SELECT_PR_CLEAR} !important`
                      : undefined,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&:hover": {
                  bgcolor: useCaseFilter !== "all" ? "#FFE0D0" : "#f5f5f5",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2932E5",
                },
              }}
            >
              <MenuItem value="all">All Use Cases</MenuItem>
              {/* {hasEmptyUseCaseInTable ? (
                <MenuItem value="">Empty / Not assigned</MenuItem>
              ) : null} */}
              {useCaseFilterOptions.map((label) => (
                <MenuItem key={label} value={label} title={label}>
                  {label.length > 72 ? `${label.slice(0, 69)}…` : label}
                </MenuItem>
              ))}
            </Select>
            {useCaseFilter !== "all" ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClearUseCaseFilter();
                }}
                aria-label="Clear use case filter"
                sx={{
                  position: "absolute",
                  right: `${ZONE_MGMT_FILTER_CLEAR_BTN_RIGHT_PX}px`,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  p: 0.25,
                  width: 28,
                  height: 28,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null}
          </FormControl>

          {/* Spacer keeps filters left; Add Camera pinned right */}
          <Box sx={{ flex: 1, minWidth: 8 }} />

          {onAddCameraApply && canCreateCamera ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openAddCameraDialog()}
              aria-label="Add camera"
              sx={{
                flexShrink: 0,
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 600,
                bgcolor: "#2932E5",
                borderRadius: "10px",
                height: 36,
                px: 2,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#1e28b8",
                  boxShadow: "none",
                },
              }}
            >
              Add Camera
            </Button>
          ) : null}
        </Box>
      </Box>

      {onAddCameraApply || onEditCameraApply ? (
        <AddCameraDialog
          open={addCameraOpen}
          onClose={closeCameraDialog}
          onApply={async (values) => {
            if (!onAddCameraApply) {
              throw new Error("ADD_CAMERA_HANDLED");
            }
            await onAddCameraApply(values);
          }}
          editingCameraId={editingCameraId}
          onEditApply={onEditCameraApply}
          apiBase={AI_DETECTION_API_BASE}
        />
      ) : null}

      {/* Content: error / loading / table or grid – same pattern as Manage Use Case tab */}
      {camerasError ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            p: 4,
            bgcolor: "#FAFAFA",
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 48, color: "#B71C1C" }} />
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "16px",
              color: "#4A5565",
              textAlign: "center",
            }}
          >
            {camerasError}
          </Typography>
          {onRetry && (
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={onRetry}
              sx={{
                bgcolor: "#2932E5",
                textTransform: "none",
                "&:hover": { bgcolor: "#1f28b8" },
              }}
            >
              Retry
            </Button>
          )}
        </Box>
      ) : camerasLoading ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
            bgcolor: "#FAFAFA",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#6B7280",
            }}
          >
            Loading cameras...
          </Typography>
        </Box>
      ) : displayRows.length === 0 || filteredAndSortedZones.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            p: 4,
            bgcolor: "#FAFAFA",
          }}
        >
          <MapIcon sx={{ fontSize: 100, color: "#9CA3AF" }} />
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "16px",
              color: "#6B7280",
              textAlign: "center",
            }}
          >
            {displayRows.length === 0
              ? "No camera is available"
              : "No results found"}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {/* List View */}
          {viewMode === "list" && (
            <TableContainer
              sx={{
                flexGrow: 1,
                overflow: "auto",
                maxWidth: "100%",
                minHeight: 0,
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{ tableLayout: "fixed", width: "100%" }}
              >
                <TableHead>
                  <TableRow sx={{ height: "23.2px" }}>
                    {/* ID - 80px */}
                    <TableCell
                      onClick={() => handleSort("id")}
                      sx={{
                        cursor: "pointer",
                        userSelect: "none",
                        width: 80,
                        minWidth: 80,
                        textAlign: "left",
                        px: 1, // 8px
                        py: 1, // 8px - Changed from 0.75 (6px)
                        height: "23.2px",
                        bgcolor: "#FAFAFA",
                        "&:hover": {
                          bgcolor: "rgba(41, 50, 229, 0.06)",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#2932E5",
                            letterSpacing: "0.1px",
                          }}
                        >
                          ID
                        </Typography>
                        {orderBy === "id" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ) : (
                            <ArrowDownwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ))}
                      </Box>
                    </TableCell>

                    {/* Camera Group - 140px */}
                    <TableCell
                      onClick={() => handleSort("cameraGroup")}
                      sx={{
                        cursor: "pointer",
                        userSelect: "none",
                        width: 140,
                        minWidth: 140,
                        textAlign: "left",
                        px: 1, // 8px
                        py: 0.75, // 6px
                        height: "23.2px",
                        bgcolor: "#FAFAFA",
                        "&:hover": {
                          bgcolor: "rgba(41, 50, 229, 0.06)",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#2932E5",
                            letterSpacing: "0.1px",
                          }}
                        >
                          Camera Group
                        </Typography>
                        {orderBy === "cameraGroup" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ) : (
                            <ArrowDownwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ))}
                      </Box>
                    </TableCell>

                    {/* Camera Name - flex */}
                    <TableCell
                      onClick={() => handleSort("cameraName")}
                      sx={{
                        cursor: "pointer",
                        userSelect: "none",
                        minWidth: 200,
                        textAlign: "left",
                        width: "200px",
                        px: 1, // 8px
                        py: 0.5, // 6px
                        height: "23.2px",
                        bgcolor: "#FAFAFA",
                        "&:hover": {
                          bgcolor: "rgba(41, 50, 229, 0.06)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          width: "150px",
                          minWidth: "150px",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#2932E5",
                            letterSpacing: "0.1px",
                          }}
                        >
                          Camera Name
                        </Typography>
                        {orderBy === "cameraName" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ) : (
                            <ArrowDownwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ))}
                      </Box>
                    </TableCell>

                    {/* streaming_url from GET /cameras */}
                    <TableCell
                      onClick={() => handleSort("streamingUrl")}
                      sx={{
                        cursor: "pointer",
                        userSelect: "none",
                        width: 220,
                        minWidth: 180,
                        maxWidth: 320,
                        textAlign: "left",
                        px: 1, // 8px
                        py: 0.75, // 6px
                        height: "23.2px",
                        bgcolor: "#FAFAFA",
                        "&:hover": {
                          bgcolor: "rgba(41, 50, 229, 0.06)",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#2932E5",
                            letterSpacing: "0.1px",
                          }}
                        >
                          streaming_url
                        </Typography>
                        {orderBy === "streamingUrl" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ) : (
                            <ArrowDownwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ))}
                      </Box>
                    </TableCell>

                    {/* Zone - 80px */}
                    <TableCell
                      onClick={() => handleSort("zone")}
                      sx={{
                        cursor: "pointer",
                        userSelect: "none",
                        width: 80,
                        minWidth: 80,
                        textAlign: "left",
                        px: 1, // 8px
                        py: 0.75, // 6px
                        height: "23.2px",
                        bgcolor: "#FAFAFA",
                        "&:hover": {
                          bgcolor: "rgba(41, 50, 229, 0.06)",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#2932E5",
                            letterSpacing: "0.1px",
                          }}
                        >
                          Zone
                        </Typography>
                        {orderBy === "zone" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ) : (
                            <ArrowDownwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ))}
                      </Box>
                    </TableCell>

                    {/* Use Case - 180px */}
                    <TableCell
                      onClick={() => handleSort("useCase")}
                      sx={{
                        cursor: "pointer",
                        userSelect: "none",
                        width: 180,
                        minWidth: 180,
                        textAlign: "left",
                        px: 1, // 8px
                        py: 0.75, // 6px
                        height: "23.2px",
                        bgcolor: "#FAFAFA",
                        "&:hover": {
                          bgcolor: "rgba(41, 50, 229, 0.06)",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#2932E5",
                            letterSpacing: "0.1px",
                          }}
                        >
                          Use Case
                        </Typography>
                        {orderBy === "useCase" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ) : (
                            <ArrowDownwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ))}
                      </Box>
                    </TableCell>

                    {/* Detection status: Active/Inactive chip + enable switch */}
                    <TableCell
                      onClick={() => handleSort("status")}
                      sx={{
                        cursor: "pointer",
                        userSelect: "none",
                        width: 200,
                        minWidth: 200,
                        textAlign: "left",
                        px: 1, // 8px
                        py: 0.75, // 6px
                        height: "23.2px",
                        bgcolor: "#FAFAFA",
                        "&:hover": {
                          bgcolor: "rgba(41, 50, 229, 0.06)",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#2932E5",
                            letterSpacing: "0.1px",
                          }}
                        >
                          Detection Status
                        </Typography>
                        {orderBy === "status" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ) : (
                            <ArrowDownwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ))}
                      </Box>
                    </TableCell>

                    {/* Actions — label visible while any row is hovered or the row menu is open (same as ⋮). */}
                    <TableCell
                      align="center"
                      scope="col"
                      aria-label="Actions"
                      sx={{
                        width: 56,
                        minWidth: 56,
                        px: 0.5,
                        py: 0.75,
                        height: "23.2px",
                        bgcolor: "#FAFAFA",
                      }}
                    >
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#2932E5",
                          letterSpacing: "0.1px",
                          opacity:
                            hoveredRowId != null || anchorEl != null ? 1 : 0,
                          transition: "opacity 0.15s ease",
                          minHeight: 20,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        Actions
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedZones.map((zone, index) => {
                    const isHovered = hoveredRowId === zone.id;
                    const displayId =
                      zone.srId?.trim() ||
                      (/^ZN-\d{3}$/.test(zone.id ?? "")
                        ? (zone.id ?? "")
                        : `ZN-${String(page * rowsPerPage + index + 1).padStart(3, "0")}`);

                    return (
                      <TableRow
                        key={zone.id}
                        onMouseEnter={() => setHoveredRowId(zone.id)}
                        onMouseLeave={() => setHoveredRowId(null)}
                        onContextMenu={(e) => handleContextMenu(e, zone)}
                        onDoubleClick={() => openZoneConfiguration(zone, !canUpdateZone)}
                        sx={{
                          height: 30,
                          cursor: "pointer",
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            bgcolor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        {/* ID - display as ZN-001, ZN-002, ... */}
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            px: 1, // 8px
                            py: 0.5, // 4px
                            height: 30,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "14px",
                            }}
                          >
                            {displayId}
                          </Typography>
                        </TableCell>

                        {/* Camera Group */}
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            px: 1, // 8px
                            py: 0.5, // 4px
                            height: 30,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              color: "text.secondary",
                            }}
                          >
                            {cellOrDash(zone.cameraGroup)}
                          </Typography>
                        </TableCell>

                        {/* Camera Name */}
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            overflow: "hidden",
                            px: 1, // 8px
                            py: 0.5, // 4px
                            height: 30,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              overflow: "hidden",
                            }}
                          >
                            <CameraIcon
                              fontSize="small"
                              sx={{ color: "action.active", flexShrink: 0 }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "14px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                              }}
                            >
                              {cell(zone.cameraName)}
                            </Typography>

                            {/* Favorite Star - After Camera Name */}
                            <IconButton
                              size="small"
                              onClick={(e) => handleToggleFavorite(zone.id, e)}
                              aria-label={
                                zone.isFavorite
                                  ? "Remove from favorites"
                                  : "Add to favorites"
                              }
                              sx={{
                                padding: 0,
                                minWidth: "auto",
                                opacity: isHovered ? 1 : 0,
                                transition: "opacity 0.2s",
                                flexShrink: 0,
                                ml: 0.5,
                                pointerEvents: isHovered ? "auto" : "none",
                              }}
                            >
                              {zone.isFavorite ? (
                                <StarIcon
                                  sx={{ fontSize: 16, color: "#FF8555" }}
                                />
                              ) : (
                                <StarBorderIcon
                                  sx={{ fontSize: 16, color: "#9CA3AF" }}
                                />
                              )}
                            </IconButton>
                          </Box>
                        </TableCell>

                        {/* streaming_url from backend */}
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            px: 1, // 8px
                            py: 0.5, // 4px
                            height: 30,
                            maxWidth: 320,
                          }}
                        >
                          {(() => {
                            const urlText = cell(zone.streamingUrl);
                            const showTip = urlText.length > 48;
                            const short = showTip
                              ? `${urlText.slice(0, 45)}…`
                              : urlText;
                            const body = (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "13px",
                                  color: "text.secondary",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {short || "—"}
                              </Typography>
                            );
                            return showTip ? (
                              <Tooltip
                                title={urlText}
                                {...GLOBAL_TOOLTIP_PROPS}
                              >
                                {body}
                              </Tooltip>
                            ) : (
                              body
                            );
                          })()}
                        </TableCell>

                        {/* Zone - Hyperlink */}
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            px: 1, // 8px
                            py: 0.5, // 4px
                            height: 30,
                          }}
                        >
                          <Link
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              openZoneConfiguration(zone, !canUpdateZone);
                            }}
                            sx={{
                              color: "#2932E5",
                              textDecoration: "none",
                              fontWeight: 500,
                              fontSize: "14px",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {cell(zone.zone)}
                          </Link>
                        </TableCell>

                        {/* Use Case - show "--" when blank by default */}
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            px: 1, // 8px
                            py: 0.5, // 4px
                            height: 30,
                          }}
                        >
                          {(() => {
                            const useCaseDisplay = cell(zone.useCase) || "--";
                            return useCaseDisplay !== "--" &&
                              useCaseDisplay.length > 20 ? (
                              <Tooltip
                                title={useCaseDisplay}
                                {...GLOBAL_TOOLTIP_PROPS}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "13px",
                                    color: "text.secondary",
                                  }}
                                >
                                  {truncateText(useCaseDisplay, 20)}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "13px",
                                  color: "text.secondary",
                                }}
                              >
                                {useCaseDisplay}
                              </Typography>
                            );
                          })()}
                        </TableCell>

                        {/* Detection status: label + toggle */}
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            px: 1, // 8px
                            py: 0.5, // 4px
                            height: 30,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-start",
                              gap: 1,
                              flexWrap: "nowrap",
                            }}
                          >
                            <Chip
                              label={cell(zone.status)}
                              size="small"
                              sx={{
                                bgcolor:
                                  (zone.status ?? "") === "Active"
                                    ? "#e8f5e9"
                                    : "#f5f5f5",
                                color:
                                  (zone.status ?? "") === "Active"
                                    ? "#2e7d32"
                                    : "#757575",
                                height: "20px",
                                fontSize: "11px",
                                flexShrink: 0,
                              }}
                            />
                            <Switch
                              checked={zone.enabled}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleToggle(zone);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              size="small"
                              inputProps={{
                                "aria-label": `Detection ${
                                  zone.enabled ? "on" : "off"
                                } for ${cell(zone.cameraName) || "camera"}`,
                              }}
                              sx={{
                                flexShrink: 0,
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: "#2932E5",
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                  {
                                    backgroundColor: "#2932E5",
                                  },
                              }}
                            />
                          </Box>
                        </TableCell>

                        {/* Actions */}
                        <TableCell
                          align="center"
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            px: 0.5,
                            py: 0.5,
                            height: 30,
                            width: 56,
                            minWidth: 56,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, zone)}
                            aria-label="Open row actions"
                            sx={{
                              color: "text.secondary",
                              opacity: isHovered ? 1 : 0,
                              transition: "opacity 0.2s ease",
                              pointerEvents: isHovered ? "auto" : "none",
                              "&:focus-visible": {
                                opacity: 1,
                                pointerEvents: "auto",
                              },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Grid view — card layout aligned with ManageUseCaseTable */}
          {viewMode === "grid" && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                p: { xs: 0.5, sm: 0 },
              }}
            >
              <Grid container spacing={1}>
                {paginatedZones.map((zone, index) => {
                  const displayId =
                    zone.srId?.trim() ||
                    (/^ZN-\d{3}$/.test(zone.id ?? "")
                      ? (zone.id ?? "")
                      : `ZN-${String(page * rowsPerPage + index + 1).padStart(3, "0")}`);

                  const previewPath =
                    (zone.preview_url && String(zone.preview_url).trim()) ||
                    (zone.cameraApiId
                      ? cameraPreviewPath(zone.cameraApiId)
                      : "");
                  const resolvedThumb =
                    previewPath && !gridThumbBrokenIds.has(zone.id)
                      ? resolveCameraMediaUrl(
                          AI_DETECTION_API_BASE,
                          previewPath,
                        )
                      : "";
                  const showPreviewImg = Boolean(resolvedThumb);
                  const statusActive = (zone.status ?? "") === "Active";

                  return (
                    <Grid
                      item
                      key={zone.id}
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      xl={2}
                      sx={{ display: "flex", minWidth: 0 }}
                      onMouseEnter={() => setHoveredRowId(zone.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                    >
                      <Card
                        onDoubleClick={() => openZoneConfiguration(zone, !canUpdateZone)}
                        onContextMenu={(e) => handleContextMenu(e, zone)}
                        sx={{
                          position: "relative",
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                          minWidth: 0,
                          height: { xs: "220px", sm: "236px" },
                          minHeight: { xs: 200, sm: 236 },
                          bgcolor: "rgb(255, 255, 255)",
                          color: "rgb(33, 33, 33)",
                          boxShadow: "var(--Paper-shadow)",
                          backgroundImage: "var(--Paper-overlay)",
                          overflow: "hidden",
                          borderRadius: "14px",
                          transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          cursor: "pointer",
                          border: "1px solid rgb(229, 231, 235)",
                          "&:hover": {
                            boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
                            transform: "translateY(-4px) scale(1.02)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            minWidth: 0,
                            paddingLeft: { xs: 1, sm: "10px" },
                            paddingRight: { xs: 1, sm: "10px" },
                            pt: 1,
                            pb: 0,
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              maxWidth: "100%",
                              height: { xs: "96px", sm: "112.5px" },
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "rgb(33, 33, 33)",
                              borderRadius: "8px",
                              overflow: "hidden",
                              position: "relative",
                            }}
                          >
                            {showPreviewImg ? (
                              <AlertHistoryMediaImage
                                href={resolvedThumb}
                                alt={
                                  cell(zone.cameraName)
                                    ? `Preview: ${cell(zone.cameraName)}`
                                    : "Camera preview"
                                }
                                onError={() => {
                                  setGridThumbBrokenIds((prev) => {
                                    if (prev.has(zone.id)) return prev;
                                    const next = new Set(prev);
                                    next.add(zone.id);
                                    return next;
                                  });
                                }}
                                sx={{
                                  position: "absolute",
                                  inset: 0,
                                  width: "100%",
                                  height: "100%",
                                }}
                                imgSx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <ImagePlaceholderIcon
                                sx={{
                                  fontSize: { xs: 40, sm: 44 },
                                  color: "rgba(255,255,255,0.45)",
                                }}
                                aria-hidden
                              />
                            )}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMenuClick(e, zone);
                              }}
                              aria-label="Open actions menu"
                              sx={{
                                position: "absolute",
                                top: 8,
                                left: 8,
                                zIndex: 1,
                                color: "rgba(255,255,255,0.95)",
                                bgcolor: "rgba(255,255,255,0.15)",
                                padding: "6px",
                                "&:hover": {
                                  bgcolor: "rgba(255,255,255,0.3)",
                                  color: "#fff",
                                },
                              }}
                            >
                              <MoreVertIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                            <Tooltip
                              title={
                                zone.isFavorite
                                  ? "Remove from favorites"
                                  : "Add to favorites"
                              }
                              {...GLOBAL_TOOLTIP_PROPS}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) =>
                                  handleToggleFavorite(zone.id, e)
                                }
                                aria-label={
                                  zone.isFavorite
                                    ? "Remove from favorites"
                                    : "Add to favorites"
                                }
                                sx={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  zIndex: 1,
                                  color: zone.isFavorite
                                    ? "#FFB400"
                                    : "rgba(255,255,255,0.95)",
                                  bgcolor: "rgba(255,255,255,0.15)",
                                  padding: "6px",
                                  "&:hover": {
                                    bgcolor: "rgba(255,255,255,0.3)",
                                    color: zone.isFavorite ? "#FFB400" : "#fff",
                                  },
                                }}
                              >
                                {zone.isFavorite ? (
                                  <StarIcon sx={{ fontSize: 20 }} />
                                ) : (
                                  <StarBorderIcon sx={{ fontSize: 20 }} />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 10,
                                left: 10,
                                zIndex: 1,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: "20px",
                                bgcolor: statusActive
                                  ? "rgba(76, 175, 80, 0.92)"
                                  : "rgba(158, 158, 158, 0.92)",
                                color: "#fff",
                                fontFamily: "Roboto, sans-serif",
                                fontSize: "11px",
                                fontWeight: 600,
                                letterSpacing: "0.02em",
                                textTransform: "uppercase",
                              }}
                            >
                              {statusActive ? "ACTIVE" : "INACTIVE"}
                            </Box>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            p: { xs: 1, sm: 1.5 },
                            pt: { xs: 1, sm: 1.25 },
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            overflow: "hidden",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: { xs: "14px", sm: "16px" },
                              fontWeight: 700,
                              color: "#212121",
                              mb: 0.5,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                          >
                            {cell(zone.cameraName)}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: { xs: "12px", sm: "13px" },
                              color: "#4A5565",
                              mb: 0.25,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                          >
                            {cell(zone.useCase) || "—"}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: { xs: "12px", sm: "13px" },
                              color: "#4A5565",
                              mb: 0.5,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                          >
                            ID: {displayId}
                            {(() => {
                              const su = cell(zone.streamingUrl);
                              if (!su) return "";
                              const short =
                                su.length > 36 ? `${su.slice(0, 33)}…` : su;
                              return ` · ${short}`;
                            })()}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              mt: "auto",
                              pt: 0.25,
                              minWidth: 0,
                              flexWrap: "wrap",
                            }}
                          >
                            <Link
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openZoneConfiguration(zone, !canUpdateZone);
                              }}
                              sx={{
                                color: "#2932E5",
                                textDecoration: "none",
                                fontFamily: "Roboto, sans-serif",
                                fontSize: { xs: "12px", sm: "13px" },
                                fontWeight: 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                minWidth: 0,
                                "&:hover": {
                                  textDecoration: "underline",
                                },
                              }}
                            >
                              {cell(zone.zone) !== "" && cell(zone.zone) != null
                                ? `Zone ${zone.zone}`
                                : "Configure"}
                            </Link>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                flexShrink: 0,
                                ml: "auto",
                              }}
                            >
                              <Chip
                                label={cell(zone.status)}
                                size="small"
                                sx={{
                                  bgcolor:
                                    (zone.status ?? "") === "Active"
                                      ? "#e8f5e9"
                                      : "#f5f5f5",
                                  color:
                                    (zone.status ?? "") === "Active"
                                      ? "#2e7d32"
                                      : "#757575",
                                  height: "20px",
                                  fontSize: "10px",
                                }}
                              />
                              <Switch
                                checked={zone.enabled}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleToggle(zone);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: "#2932E5",
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                    {
                                      backgroundColor: "#2932E5",
                                    },
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* Pagination */}
          <Box
            sx={{
              flexShrink: 0,
              width: "100%",
              borderTop: "1px solid #E5E7EB",
              borderBottom: "1px solid #E5E7EB",
              bgcolor: "#f2f2f2",
            }}
          >
            <TablePagination
              component="div"
              count={filteredAndSortedZones.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[14]}
              sx={{
                border: "none",
                "& .MuiTablePagination-toolbar": {
                  minHeight: 48,
                  px: 2,
                  bgcolor: "transparent",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  {
                    fontSize: 14,
                    fontFamily: "Roboto, sans-serif",
                    color: "#4A5565",
                    my: 0,
                  },
              }}
            />
          </Box>
        </Box>
      )}

      {/* Actions menu: 3-dot button or right-click (list row + grid card) */}
      <Menu
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          anchorEl
            ? { top: anchorEl.mouseY + 2, left: anchorEl.mouseX + 2 }
            : undefined
        }
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem
          onClick={() => {
            if (anchorEl?.zone) {
              openZoneConfiguration(anchorEl.zone, true);
            }
            handleMenuClose();
          }}
        >
          <InfoIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        {canUpdateZone ? (
        <MenuItem
          onClick={() => {
            if (anchorEl?.zone) {
              openZoneConfiguration(anchorEl.zone, false);
            }
            handleMenuClose();
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit Zone
        </MenuItem>
        ) : null}
        {onEditCameraApply && canUpdateCamera ? (
          <MenuItem
            disabled={
              !anchorEl?.zone || !isUsableCameraApiId(anchorEl.zone.cameraApiId)
            }
            onClick={() => {
              const z = anchorEl?.zone;
              const cid = z?.cameraApiId?.trim();
              if (!z || !cid || !isUsableCameraApiId(cid)) {
                handleMenuClose();
                return;
              }
              setEditingCameraId(cid);
              setAddCameraOpen(true);
              handleMenuClose();
            }}
          >
            <CameraIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        ) : null}
        {onDeleteCamera && canDeleteCamera ? (
          <>
            <Divider />
            <MenuItem
              disabled={
                !anchorEl?.zone ||
                !isUsableCameraApiId(anchorEl.zone.cameraApiId)
              }
              onClick={() => {
                const z = anchorEl?.zone;
                const cid = z?.cameraApiId?.trim();
                if (!z || !cid || !isUsableCameraApiId(cid)) {
                  handleMenuClose();
                  return;
                }
                setDeleteUpcomingFeatureOpen(true);
                handleMenuClose();
              }}
              sx={{ color: "error.main" }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Delete
            </MenuItem>
          </>
        ) : null}
      </Menu>

      <Dialog
        open={deleteUpcomingFeatureOpen}
        onClose={() => setDeleteUpcomingFeatureOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="delete-camera-upcoming-feature-title"
      >
        <DialogTitle
          id="delete-camera-upcoming-feature-title"
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: 18,
            fontWeight: 600,
            color: "#000000",
          }}
        >
          Delete
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              color: "#212121",
            }}
          >
            Upcoming feature
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, justifyContent: "flex-end" }}>
          <Button
            onClick={() => setDeleteUpcomingFeatureOpen(false)}
            variant="contained"
            color="primary"
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontWeight: 500,
              borderRadius: 1,
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Date Range Dialog */}
      <Dialog
        open={customDateDialogOpen}
        onClose={handleCustomDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "18px",
            fontWeight: 500,
          }}
        >
          Custom Date Range
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Start Date"
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "flex-start",
            px: 2,
            pb: 2,
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Button onClick={handleCustomDialogClose} sx={{ color: "#4A5565" }}>
            Cancel
          </Button>
          <Button
            onClick={handleApplyCustomDateRange}
            variant="contained"
            disabled={!customStartDate.trim() && !customEndDate.trim()}
            sx={{
              bgcolor: "#2932E5",
              "&:hover": { bgcolor: "#1f28b8" },
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
